import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, ModelType, ImageGenerationMode, VideoClipStatus, ImageGenerationParams, IUserModel, ResourceType, ResourceStatus, IVideoClipResource, Resolution, IEpisode } from '@packages/global';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { promptHelperApi } from '../../../services/user/promptHelper.api';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import { FrameTypeSelector, ReferenceImagesSection, ImageResourceList, ImageToolbarSection } from './components/ImageGeneration';
import { PromptInputSection, PromptEditModal } from './components';

interface ImageGenerationFormProps {
  videoClip: IVideoClip;
  models: IUserModel[];
  modelsLoading: boolean;
  frameType?: 'start' | 'end' | 'storyboard';
  onFrameTypeChange?: (type: 'start' | 'end' | 'storyboard') => void;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  editConfig?: import('@packages/global').VideoEditConfig | null;
  // 外部传入的资源状态（来自统一轮询）
  resources?: IVideoClipResource[];
  isPolling?: boolean;
  onTriggerPoll?: () => Promise<void>;
  episode?: IEpisode | null;
  // 外部注入参考图（如无限画布连线），nonce 变化即触发一次注入
  externalAddImage?: { url: string; nonce: number };
  onReferenceImageCleared?: (url: string) => void;
  onCanvasSubmitSuccess?: () => void;
  initialParams?: Record<string, unknown>;
  canvasMode?: boolean;
}



export default function ImageGenerationForm({
  videoClip,
  models,
  modelsLoading,
  frameType,
  onFrameTypeChange,
  refreshVideoClip,
  editConfig,
  resources: externalResources = [],
  isPolling = false,
  onTriggerPoll,
  episode,
  externalAddImage,
  onReferenceImageCleared,
  onCanvasSubmitSuccess,
  initialParams,
  canvasMode = false
}: ImageGenerationFormProps) {
  const [type, setType] = useState<'start' | 'end' | 'storyboard'>(frameType || 'start');
  const [generateParams, setGenerateParams] = useState<ImageGenerationParams & { mode: ImageGenerationMode }>(() => ({
    prompt: '',
    modelId: '',
    referenceImages: [],
    mode: ImageGenerationMode.IMAGE_TO_IMAGE
  }));
  const [savedModelIds, setSavedModelIds] = useState<Record<ImageGenerationMode, string>>({
    [ImageGenerationMode.TEXT_TO_IMAGE]: '',
    [ImageGenerationMode.IMAGE_TO_IMAGE]: ''
  });
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [tempPrompt, setTempPrompt] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { alert, confirm } = useDialog();
  const { success } = useToast();
  const { t } = useTranslation(['editor', 'common']);

  // 根据当前帧类型过滤资源
  const getResourceType = (frameType: 'start' | 'end' | 'storyboard'): ResourceType => {
    switch (frameType) {
      case 'start': return ResourceType.START_IMAGE;
      case 'end': return ResourceType.END_IMAGE;
      case 'storyboard': return ResourceType.STORYBOARD_IMAGE;
    }
  };

  const filteredResources = externalResources
    .filter((r: any) => r.resourceType === getResourceType(type))
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // 检查当前帧类型是否有进行中的任务
  const isGenerating = {
    start: externalResources.some((r: any) =>
      r.resourceType === ResourceType.START_IMAGE &&
      (r.status === ResourceStatus.PENDING || r.status === ResourceStatus.GENERATING)
    ),
    end: externalResources.some((r: any) =>
      r.resourceType === ResourceType.END_IMAGE &&
      (r.status === ResourceStatus.PENDING || r.status === ResourceStatus.GENERATING)
    ),
    storyboard: externalResources.some((r: any) =>
      r.resourceType === ResourceType.STORYBOARD_IMAGE &&
      (r.status === ResourceStatus.PENDING || r.status === ResourceStatus.GENERATING)
    )
  };

  const handleReuseParams = (params: any) => {
    setGenerateParams(prev => {
      // 处理参考图片，支持多种可能的字段名
      let images: string[] = [];

      // 优先使用非空数组
      if (Array.isArray(params.referenceImages) && params.referenceImages.length > 0) {
        images = params.referenceImages;
      } else if (Array.isArray(params.imageUrls) && params.imageUrls.length > 0) {
        images = params.imageUrls;
      }
      // 其次尝试字符串
      else if (typeof params.referenceImages === 'string' && params.referenceImages) {
        images = [params.referenceImages];
      } else if (typeof params.referenceImage === 'string' && params.referenceImage) {
        images = [params.referenceImage];
      }
      // 最后如果 referenceImages 是空数组，也接受
      else if (Array.isArray(params.referenceImages)) {
        images = [];
      }

      // 如果提取到了图片，就使用提取到的图片
      // 如果 params 中显式包含了 referenceImages (即使是空数组)，也使用它
      // 只有当完全没有相关字段时，才考虑保留 prev (或者默认为空)
      const hasImageField = params.referenceImages !== undefined || params.referenceImage !== undefined || params.imageUrls !== undefined;
      const finalImages = hasImageField ? images : (prev.referenceImages || []);

      // 根据图片数量推断模式：有参考图时默认图生图
      let mode: ImageGenerationMode;
      if (finalImages.length === 0) {
        mode = ImageGenerationMode.TEXT_TO_IMAGE;
      } else {
        // 有参考图时默认图生图，除非保存的参数明确指定了模式
        mode = params.mode || ImageGenerationMode.IMAGE_TO_IMAGE;
      }

      // 根据新模式和图片数量过滤可用模型
      const availableModels = models.filter(model => {
        const imgCount = finalImages.length;
        if (imgCount === 0) {
          return model.modelType === ModelType.TEXT_TO_IMAGE;
        } else if (imgCount === 1) {
          if (mode === ImageGenerationMode.TEXT_TO_IMAGE) {
            return model.modelType === ModelType.TEXT_TO_IMAGE && model.imageInputCount >= 1;
          } else {
            return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
          }
        } else {
          return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= imgCount;
        }
      });

      const modelId = availableModels.some(m => m.id === params.modelId)
        ? params.modelId
        : (availableModels.length > 0 ? availableModels[0].id : prev.modelId);

      return {
        ...prev,
        modelId,
        prompt: params.prompt || prev.prompt,
        mode,
        referenceImages: finalImages
      };
    });
  };

  const handleApplyImage = async (imageUrl: string, targetType: 'start' | 'end' | 'storyboard') => {
    try {
      let updateData: any;
      if (targetType === 'start') {
        updateData = { startImage: imageUrl, status: VideoClipStatus.COMPLETED };
      } else if (targetType === 'end') {
        updateData = { endImage: imageUrl, status: VideoClipStatus.COMPLETED };
      } else {
        updateData = { storyboardImage: imageUrl, status: VideoClipStatus.COMPLETED };
      }

      await videoClipApi.updateVideoClip(videoClip.id, updateData);

      // 先刷新 videoClip 数据，确保时间线能获取到最新的图片
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }

      // 注意：应用图片不应该改变duration、trimStart或trimEnd
      // 这些值应该保持不变，由用户手动调整或在应用音频/视频时调整

      const typeLabels = { start: t('common:appliedToStart'), end: t('common:appliedToEnd'), storyboard: t('common:appliedToStoryboard') };
      success(typeLabels[targetType]);
    } catch (error) {
      console.error('应用图片失败:', error);
      alert(t('common:imageApplyFailed'));
    }
  };

  // 引用图片作为参考图
  const handleReferenceImage = (imageUrl: string) => {
    if (!canAddMoreImages) {
      alert(t('common:refImageLimit'));
      return;
    }
    setGenerateParams(prev => ({
      ...prev,
      referenceImages: [...(prev.referenceImages || []), imageUrl],
      mode: ImageGenerationMode.IMAGE_TO_IMAGE
    }));
    success(t('common:addedAsRef'));
  };



  // 同步外部的 frameType 到内部 state
  useEffect(() => {
    if (frameType && frameType !== type) {
      setType(frameType);
    }
  }, [frameType]);

  // 根据图片数量自动确定模式
  const getAutoMode = (imageCount: number): ImageGenerationMode => {
    if (imageCount === 0) return ImageGenerationMode.TEXT_TO_IMAGE;
    // 1张图片时默认为编辑模式，2+张图片也是编辑模式
    return ImageGenerationMode.IMAGE_TO_IMAGE;
  };

  const imageCount = generateParams.referenceImages?.length || 0;
  const shouldShowModeSelector = imageCount === 1;

  // 根据图片数量实时计算当前模式
  const currentMode = imageCount === 1 ? generateParams.mode : getAutoMode(imageCount);

  // 根据当前模式和图片数量过滤模型
  const filteredModels = models.filter(model => {
    if (imageCount === 0) {
      return model.modelType === ModelType.TEXT_TO_IMAGE;
    } else if (imageCount === 1) {
      if (currentMode === ImageGenerationMode.TEXT_TO_IMAGE) {
        return model.modelType === ModelType.TEXT_TO_IMAGE && model.imageInputCount >= 1;
      } else {
        return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
      }
    } else {
      return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= imageCount;
    }
  });

  // 确保选中的模型在过滤后的列表中，否则自动选择第一个
  useEffect(() => {
    if (filteredModels.length > 0 && !filteredModels.some(m => m.id === generateParams.modelId)) {
      setGenerateParams(prev => ({
        ...prev,
        modelId: filteredModels[0].id
      }));
    }
  }, [filteredModels.length, generateParams.modelId]);
  const selectedModel = models.find(m => m.id === generateParams.modelId);
  const supportsReferenceImage = selectedModel && (selectedModel.imageInputCount || 0) > 0;

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [externalResources.length]);

  // 切换片段或帧类型时，加载对应的参数
  useEffect(() => {
    let paramsField: string | undefined;
    if (type === 'start') {
      paramsField = videoClip.lastStartImageParams;
    } else if (type === 'end') {
      paramsField = videoClip.lastEndImageParams;
    } else {
      paramsField = videoClip.lastStoryboardImageParams;
    }

    if (paramsField) {
      try {
        const params = JSON.parse(paramsField);
        handleReuseParams(params);
      } catch (e) {
        console.warn('Failed to parse last params:', e);
      }
    } else {
      // 没有保存的参数，清空提示词（不使用画面描述作为默认值）
      if (type === 'storyboard' && (videoClip.startImage || videoClip.endImage)) {
        // 分镜图片且无历史参数时，自动将开始帧/结束帧作为参考图
        const imagesToAdd: string[] = [];
        if (videoClip.startImage) imagesToAdd.push(videoClip.startImage);
        if (videoClip.endImage && videoClip.endImage !== videoClip.startImage) {
          imagesToAdd.push(videoClip.endImage);
        }

        const newMode = ImageGenerationMode.IMAGE_TO_IMAGE;
        const availableModels = models.filter(model => {
          const imgCount = imagesToAdd.length;
          if (imgCount === 1) {
            return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
          } else {
            return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= imgCount;
          }
        });

        setGenerateParams(prev => {
          const newModelId = availableModels.some(m => m.id === prev.modelId)
            ? prev.modelId
            : (availableModels.length > 0 ? availableModels[0].id : prev.modelId);
          return {
            prompt: '',
            modelId: newModelId,
            referenceImages: imagesToAdd,
            mode: newMode
          };
        });
      } else {
        setGenerateParams(prev => ({
          prompt: '',
          modelId: prev.modelId,
          referenceImages: [],
          mode: ImageGenerationMode.TEXT_TO_IMAGE
        }));
      }
    }
  }, [videoClip.id, type]);

  const initialParamsAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialParams || initialParamsAppliedRef.current) return;
    initialParamsAppliedRef.current = true;
    handleReuseParams(initialParams);
  }, [initialParams]);

  const addReferenceImage = (url: string) => {
    setGenerateParams(prev => {
      const newImages = [...(prev.referenceImages || []), url];
      const imgCount = newImages.length;

      // 有参考图时默认使用图生图模式
      const newMode = imgCount > 0
        ? ImageGenerationMode.IMAGE_TO_IMAGE
        : prev.mode;

      // 根据新模式和图片数量过滤可用模型
      const availableModels = models.filter(model => {
        if (imgCount === 1) {
          if (newMode === ImageGenerationMode.TEXT_TO_IMAGE) {
            return model.modelType === ModelType.TEXT_TO_IMAGE && model.imageInputCount >= 1;
          } else {
            return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
          }
        } else {
          return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= imgCount;
        }
      });

      const newModelId = availableModels.some(m => m.id === prev.modelId)
        ? prev.modelId
        : (availableModels.length > 0 ? availableModels[0].id : prev.modelId);

      return {
        ...prev,
        referenceImages: newImages,
        mode: newMode,
        modelId: newModelId
      };
    });
    setShowImageUpload(false);
  };

  // 画布连线/快捷按钮注入参考图：nonce 变化即追加一次，避免重复注入
  const lastExternalNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!externalAddImage) return;
    if (lastExternalNonceRef.current === externalAddImage.nonce) return;
    lastExternalNonceRef.current = externalAddImage.nonce;
    const url = externalAddImage.url;
    if (!url) return;
    if ((generateParams.referenceImages || []).includes(url)) return;
    if (imageCount >= maxImageCount) return;
    addReferenceImage(url);
  }, [externalAddImage]);

  const removeReferenceImage = (index: number) => {
    const removedUrl = generateParams.referenceImages?.[index];
    if (removedUrl) {
      onReferenceImageCleared?.(removedUrl);
    }
    setGenerateParams(prev => {
      const newImages = (prev.referenceImages || []).filter((_, i) => i !== index);
      const imgCount = newImages.length;

      // 根据图片数量自动调整模式
      let newMode = prev.mode;
      if (imgCount === 0) {
        newMode = ImageGenerationMode.TEXT_TO_IMAGE;
      }
      // 1张图时保持用户选择的模式
      // 2+张图时保持图生图模式

      // 根据新模式和图片数量过滤可用模型
      const availableModels = models.filter(model => {
        if (imgCount === 0) {
          return model.modelType === ModelType.TEXT_TO_IMAGE;
        } else if (imgCount === 1) {
          if (newMode === ImageGenerationMode.TEXT_TO_IMAGE) {
            return model.modelType === ModelType.TEXT_TO_IMAGE && model.imageInputCount >= 1;
          } else {
            return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
          }
        } else {
          return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= imgCount;
        }
      });

      const newModelId = availableModels.some(m => m.id === prev.modelId)
        ? prev.modelId
        : (availableModels.length > 0 ? availableModels[0].id : prev.modelId);

      return {
        ...prev,
        referenceImages: newImages,
        mode: newMode,
        modelId: newModelId
      };
    });
  };

  const handleDeleteResource = async (resourceId: string) => {
    const confirmed = await confirm(t('common:deleteResourceConfirm'), t('common:deleteResource'), 'danger');
    if (confirmed) {
      try {
        await videoClipResourceApi.deleteResource(resourceId);
        success(t('common:resourceDeleted'));
        if (onTriggerPoll) {
          await onTriggerPoll();
        }
      } catch (err) {
        console.error('删除资源失败:', err);
        alert(t('common:deleteFailed'));
      }
    }
  };

  const handleGenerate = async () => {
    if (!generateParams.modelId) {
      alert(t('audioEditForm.selectModel'));
      return;
    }

    if (!generateParams.prompt.trim()) {
      alert(t('imageGenForm.enterPrompt', { defaultValue: 'Please enter a prompt' }));
      return;
    }

    if (generateParams.mode === ImageGenerationMode.IMAGE_TO_IMAGE && (generateParams.referenceImages?.length || 0) === 0) {
      alert(t('imageGenForm.needRefImage', { defaultValue: 'Image-to-image mode requires reference images' }));
      return;
    }

    try {
      // 获取 Episode 的 resolution，默认为 720p
      const resolution = episode?.resolution || Resolution.HD_720P;
      const aspectRatio = episode?.videoAspectRatio;

      const params = {
        prompt: generateParams.prompt,
        modelId: generateParams.modelId,
        referenceImages: generateParams.referenceImages,
        mode: currentMode,
        resolution,
        aspectRatio
      };
      if (type === 'start') {
        await videoClipResourceApi.generateStartImage(videoClip.id, params);
      } else if (type === 'end') {
        await videoClipResourceApi.generateEndImage(videoClip.id, params);
      } else {
        await videoClipResourceApi.generateStoryboardImage(videoClip.id, params);
      }

      // 提交成功后触发外部轮询
      if (onTriggerPoll) {
        setTimeout(() => onTriggerPoll(), 1000);
      }

      // 提交成功后重置提示词，保留其他参数以便微调
      setGenerateParams(prev => ({ ...prev, prompt: '' }));
      onCanvasSubmitSuccess?.();

    } catch (error) {
      console.error('生成失败:', error);
      alert(t('common:submitFailed'));
    }
  };

  const handleAutoGeneratePrompt = async () => {
    setIsGeneratingPrompt(true);
    try {
      // 优先使用时间线上的实际时长（用户可能已修改），而非原始 clip.duration
      const clipEditInfo = editConfig?.clips.find(c => c.clipId === videoClip.id);
      const effectiveDuration = clipEditInfo?.duration ?? videoClip.duration;
      const videoClipWithEffectiveDuration = { ...videoClip, duration: effectiveDuration };
      const result = await promptHelperApi.generateImagePrompt(videoClipWithEffectiveDuration, type, generateParams);
      setGenerateParams(prev => ({ ...prev, prompt: result.prompt }));

      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }

      success(t('videoGenForm.promptGenSuccess'));
    } catch (err) {
      console.error('生成提示词失败:', err);
      alert(t('videoGenForm.promptGenFailed'));
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handleModeChange = (newMode: ImageGenerationMode) => {
    setSavedModelIds(prev => ({ ...prev, [generateParams.mode]: generateParams.modelId }));

    const savedModelId = savedModelIds[newMode];

    // 重新计算过滤后的模型列表
    const newFilteredModels = models.filter(model => {
      if (newMode === ImageGenerationMode.TEXT_TO_IMAGE) {
        return model.modelType === ModelType.TEXT_TO_IMAGE && model.imageInputCount >= 1;
      } else {
        return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
      }
    });

    const modelId = savedModelId && newFilteredModels.find(m => m.id === savedModelId)
      ? savedModelId
      : (newFilteredModels.length > 0 ? newFilteredModels[0].id : '');

    setGenerateParams(prev => ({
      ...prev,
      mode: newMode,
      modelId
    }));
  };

  const isDisabled = false;
  // 根据所有支持图片的模型中的最大能力来计算最大图片数
  // 这样即使当前选中的模型不支持图片，用户也可以添加图片
  const maxImageCount = Math.max(
    ...models
      .filter(m => (m.imageInputCount || 0) > 0)
      .map(m => m.imageInputCount || 0),
    0
  );
  const canAddMoreImages = imageCount < maxImageCount;

  // 切换帧类型
  const handleFrameTypeChange = (newType: 'start' | 'end' | 'storyboard') => {
    setType(newType);
    onFrameTypeChange?.(newType);

    // 切换到分镜图片模式时，自动将开始帧和结束帧图片作为参考图片
    // 仅在没有历史参数记录的参考图时才添加默认图
    if (newType === 'storyboard' && (videoClip.startImage || videoClip.endImage)) {
      // 延迟设置，等待 useEffect 加载完 lastStoryboardImageParams 后再添加
      setTimeout(() => {
        setGenerateParams(prev => {
          const currentRefs = prev.referenceImages || [];

          // 如果历史参数已经有参考图，不再追加默认图
          if (currentRefs.length > 0) return prev;

          const imagesToAdd: string[] = [];

          // 添加开始帧（如果存在且未添加）
          if (videoClip.startImage && !currentRefs.includes(videoClip.startImage)) {
            imagesToAdd.push(videoClip.startImage);
          }
          // 添加结束帧（如果存在且未添加，且与开始帧不同）
          if (videoClip.endImage && !currentRefs.includes(videoClip.endImage) && videoClip.endImage !== videoClip.startImage) {
            imagesToAdd.push(videoClip.endImage);
          }

          if (imagesToAdd.length === 0) return prev;

          const newImages = [...imagesToAdd, ...currentRefs];

          // 有参考图时默认使用图生图模式
          const newMode = ImageGenerationMode.IMAGE_TO_IMAGE;

          // 根据新模式和图片数量过滤可用模型
          const availableModels = models.filter(model => {
            const imgCount = newImages.length;
            if (imgCount === 1) {
              return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= 1;
            } else {
              return model.modelType === ModelType.IMAGE_EDIT && model.imageInputCount >= imgCount;
            }
          });

          const newModelId = availableModels.some(m => m.id === prev.modelId)
            ? prev.modelId
            : (availableModels.length > 0 ? availableModels[0].id : prev.modelId);

          return {
            ...prev,
            referenceImages: newImages,
            mode: newMode,
            modelId: newModelId
          };
        });
      }, 100);
    }
  };

  const reorderReferenceImages = (newImages: string[]) => {
    setGenerateParams(prev => ({ ...prev, referenceImages: newImages }));
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      <div className="flex-shrink-0 px-4 py-2 border-b border-gray-800 bg-gray-800/30">
        <FrameTypeSelector
          type={type}
          isDisabled={isDisabled}
          onChange={handleFrameTypeChange}
        />
      </div>

      {!canvasMode && (
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <ImageResourceList
            resources={externalResources}
            frameType={type}
            canReference={canAddMoreImages}
            onApplyStart={(url) => handleApplyImage(url, 'start')}
            onApplyEnd={(url) => handleApplyImage(url, 'end')}
            onApplyStoryboard={(url) => handleApplyImage(url, 'storyboard')}
            onReference={handleReferenceImage}
            onReuse={handleReuseParams}
            onDelete={handleDeleteResource}
          />
          <div ref={messagesEndRef} />
        </div>
      )}

      <div className="flex-shrink-0 border-t border-gray-800 bg-gray-800/30">
        <ReferenceImagesSection
          referenceImages={generateParams.referenceImages || []}
          canAddMore={canAddMoreImages}
          maxCount={maxImageCount}
          isDisabled={isDisabled}
          projectId={videoClip.projectId}
          episodeId={videoClip.episodeId}
          models={models}
          showUpload={showImageUpload}
          onUploadToggle={setShowImageUpload}
          onAddImage={addReferenceImage}
          onRemoveImage={removeReferenceImage}
          onReorder={reorderReferenceImages}
        />

        <PromptInputSection
          prompt={generateParams.prompt}
          isDisabled={isDisabled}
          isGeneratingPrompt={isGeneratingPrompt}
          onPromptChange={(prompt) => setGenerateParams(prev => ({ ...prev, prompt }))}
          onExpandClick={() => {
            setTempPrompt(generateParams.prompt);
            setShowPromptModal(true);
          }}
          onAutoGenerateClick={handleAutoGeneratePrompt}
        />

        <ImageToolbarSection
          models={filteredModels}
          modelId={generateParams.modelId}
          mode={currentMode}
          showModeSelector={shouldShowModeSelector}
          isDisabled={isDisabled}
          modelsLoading={modelsLoading}
          hasPrompt={Boolean(generateParams.prompt)}
          resolution={episode?.resolution || Resolution.HD_720P}
          onModelChange={(modelId) => setGenerateParams(prev => ({ ...prev, modelId }))}
          onModeChange={handleModeChange}
          onSubmit={handleGenerate}
        />
      </div>

      {showPromptModal && (
        <PromptEditModal
          prompt={tempPrompt}
          onPromptChange={setTempPrompt}
          onClose={() => setShowPromptModal(false)}
          onConfirm={() => {
            setGenerateParams(prev => ({ ...prev, prompt: tempPrompt }));
            setShowPromptModal(false);
          }}
        />
      )}
    </div>
  );
}
